import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Edit2, Trash2, Loader2, GripVertical, Fuel, Car, Coffee, Droplet, Plug, Wind, Snowflake, Shirt, ShieldAlert, Accessibility, UtensilsCrossed, GlassWater, CircleDot } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { usePermissions } from "@/context/PermissionContext";

// Icon mapping for services
const iconMap = {
  "fuel": Fuel,
  "car": Car,
  "utensils": UtensilsCrossed,
  "droplet": Droplet,
  "glass-water": GlassWater,
  "plug": Plug,
  "coffee": Coffee,
  "snowflake": Snowflake,
  "plate-wheat": UtensilsCrossed,
  "shirt": Shirt,
  "wind": Wind,
  "shield-alert": ShieldAlert,
  "accessibility": Accessibility,
  "thermometer-snowflake": Snowflake,
  "circle": CircleDot,
};

const availableIcons = [
  { value: "fuel", label: "Fuel" },
  { value: "car", label: "Car/Taxi" },
  { value: "utensils", label: "Food/Catering" },
  { value: "droplet", label: "Water/WC" },
  { value: "glass-water", label: "Glass Water" },
  { value: "plug", label: "Power/GPU" },
  { value: "coffee", label: "Coffee" },
  { value: "snowflake", label: "Ice/De-Icing" },
  { value: "shirt", label: "Laundry" },
  { value: "wind", label: "Air/ASU" },
  { value: "shield-alert", label: "Security/Weapon" },
  { value: "accessibility", label: "Accessibility" },
  { value: "circle", label: "Default" },
];

export default function ServicesPage() {
  const { hasPermission } = usePermissions();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    icon: "circle",
    is_active: true
  });

  const canCreate = hasPermission("services", "create");
  const canEdit = hasPermission("services", "edit");
  const canDelete = hasPermission("services", "delete");

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get("/services");
      setServices(response.data);
    } catch (error) {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingService(null);
    setFormData({ name: "", icon: "circle", is_active: true });
    setDialogOpen(true);
  };

  const handleOpenEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      icon: service.icon || "circle",
      is_active: service.is_active
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Service name is required");
      return;
    }

    try {
      if (editingService) {
        await api.put(`/services/${editingService.id}`, formData);
        toast.success("Service updated successfully");
      } else {
        await api.post("/services", formData);
        toast.success("Service created successfully");
      }
      setDialogOpen(false);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save service");
    }
  };

  const handleDelete = async () => {
    if (!serviceToDelete) return;
    
    try {
      await api.delete(`/services/${serviceToDelete.id}`);
      toast.success(`Service "${serviceToDelete.name}" deleted`);
      setDeleteDialogOpen(false);
      setServiceToDelete(null);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete service");
    }
  };

  const handleToggleActive = async (service) => {
    if (!canEdit) {
      toast.error("You don't have permission to edit services");
      return;
    }
    
    try {
      await api.put(`/services/${service.id}`, { is_active: !service.is_active });
      toast.success(`Service ${!service.is_active ? "activated" : "deactivated"}`);
      fetchServices();
    } catch (error) {
      toast.error("Failed to update service");
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || CircleDot;
    return <IconComponent size={20} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#d4af37]" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="services-page">
      {/* Sticky Header */}
      <div className="sticky-page-header flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a] dark:text-white font-['Outfit']">Services Management</h1>
          <p className="text-[#64748b] dark:text-gray-400 mt-1">Manage flight services for the application</p>
        </div>
        <div className="flex gap-3 items-center">
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 text-lg px-4 py-2">
            {services.filter(s => s.is_active).length} Active Services
          </Badge>
          {canCreate && (
            <Button
              onClick={handleOpenCreate}
              className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
              data-testid="add-service-btn"
            >
              <Plus size={18} className="mr-2" />
              Add Service
            </Button>
          )}
        </div>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
        {services.map((service) => (
          <Card 
            key={service.id} 
            className={`border transition-all ${
              service.is_active 
                ? "border-[#e2e8f0] dark:border-gray-700" 
                : "border-red-200 dark:border-red-900 bg-red-50/50 dark:bg-red-900/20"
            }`}
            data-testid={`service-card-${service.field_key}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    service.is_active 
                      ? "bg-[#d4af37]/20 text-[#d4af37]" 
                      : "bg-gray-200 dark:bg-gray-700 text-gray-500"
                  }`}>
                    {getIcon(service.icon)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0f172a] dark:text-white">{service.name}</h3>
                    <p className="text-xs text-[#64748b] dark:text-gray-400 font-mono">{service.field_key}</p>
                  </div>
                </div>
                
                {/* Status badge */}
                <Badge 
                  variant={service.is_active ? "default" : "secondary"}
                  className={service.is_active 
                    ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300" 
                    : "bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300"
                  }
                >
                  {service.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>

              {/* System badge */}
              {service.is_system && (
                <Badge variant="outline" className="mt-3 text-xs">
                  System Service
                </Badge>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <Label htmlFor={`toggle-${service.id}`} className="text-xs text-gray-500">
                    {service.is_active ? "Active" : "Inactive"}
                  </Label>
                  <Switch
                    id={`toggle-${service.id}`}
                    checked={service.is_active}
                    onCheckedChange={() => handleToggleActive(service)}
                    disabled={!canEdit}
                    data-testid={`toggle-${service.field_key}`}
                  />
                </div>
                
                <div className="flex gap-1">
                  {canEdit && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenEdit(service)}
                      className="h-8 w-8 p-0"
                      data-testid={`edit-${service.field_key}`}
                    >
                      <Edit2 size={14} />
                    </Button>
                  )}
                  {canDelete && !service.is_system && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setServiceToDelete(service);
                        setDeleteDialogOpen(true);
                      }}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-${service.field_key}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info Card */}
      <Card className="mt-6 border-blue-200 dark:border-blue-800 bg-blue-50/50 dark:bg-blue-900/20">
        <CardContent className="p-4">
          <h4 className="font-semibold text-blue-800 dark:text-blue-300 mb-2">How Services Work</h4>
          <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1">
            <li>• <strong>All users</strong> can add new services to the system</li>
            <li>• <strong>Only Admin/Master Admin</strong> can edit or delete services</li>
            <li>• <strong>System services</strong> (marked with badge) cannot be deleted but can be deactivated</li>
            <li>• <strong>Active services</strong> appear in flight forms, filters, and statistics</li>
            <li>• Services are automatically included in backups and exports</li>
          </ul>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingService ? "Edit Service" : "Add New Service"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Service Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Hangar Storage"
                data-testid="service-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="icon">Icon</Label>
              <Select
                value={formData.icon}
                onValueChange={(value) => setFormData({ ...formData, icon: value })}
              >
                <SelectTrigger data-testid="service-icon-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {availableIcons.map((icon) => (
                    <SelectItem key={icon.value} value={icon.value}>
                      <div className="flex items-center gap-2">
                        {getIcon(icon.value)}
                        <span>{icon.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">Active</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="service-active-toggle"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]" data-testid="save-service-btn">
              {editingService ? "Update" : "Create"} Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Service</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{serviceToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
